import React, { useState } from 'react';
import { Badge, Grid, Box, Icon } from '@amzn/awsui-components-core';
import { faUserGroup } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResolvedMatch, Employee, AttendeePriority } from '../../interfaces/interface';
import { LEADER_EA_EMAIL_DATA, PRIORITY_COLORS } from '../../constants/EAHuddleConstants';
import { ParticipantSelector } from './ParticipantSelector';
import { EmployeeSearchBar } from './EmployeeSearchBar';
import { EmployeeListItem } from './EmployeeListItem';
import { ProfilePicture } from '../ProfilePicture';
import './HoverStyle.scss';
import './AttendeeCard.scss';

interface AttendeeCardProps {
  attendee: ResolvedMatch;
  onUpdate: (id: string, updates: Partial<ResolvedMatch>) => void;
  onRemove: (id: string) => void;
  isDuplicate?: boolean;
  existingEmails?: Set<string>;
  onToggleFavorite: (id: string) => void;
  onAddProxy?: (principalId: string, proxyEmployee: Employee) => void;
  hasProxy?: boolean;
  isReadOnly?: boolean;
  isEditMode?: boolean;
}

export const AttendeeCard: React.FC<AttendeeCardProps> = ({
  attendee,
  onUpdate,
  onRemove,
  isDuplicate = false,
  existingEmails = new Set(),
  onToggleFavorite,
  onAddProxy,
  hasProxy = false,
  isReadOnly = false,
  isEditMode = true
}) => {
  const handleEmployeeSelect = (employee: Employee) => {
    onUpdate(attendee.id, {
      selectedEmployee: employee,
      resolvedEmail: employee.employee_internal_email_address,
      editedName: employee.employee_name,
      status: 'manual'
    });
  };

  const handlePriorityChange = (priority: AttendeePriority) => {
    onUpdate(attendee.id, { priority });
  };

  const isProxy = !!attendee.proxyForLogin;
  const isEditable = isEditMode && !isReadOnly;
  const canAddProxy = !isProxy && isEditable && !!onAddProxy;
  const [showProxySearch, setShowProxySearch] = useState(false);
  const [showPrioritySelector, setShowPrioritySelector] = useState(false);

  const displayName = attendee.editedName || attendee.selectedEmployee?.employee_name || attendee.inputName;
  const email = attendee.resolvedEmail || attendee.manualEmail || '';

  // Create a display employee object for EmployeeListItem
  const displayEmployee: Employee | undefined = attendee.selectedEmployee
    ? {
        ...attendee.selectedEmployee,
        employee_name: displayName,
        displayName: displayName
      }
    : undefined;

  /** Render inline priority: click badge to expand selector, click option to select and collapse */
  const renderPriority = () => {
    if (isProxy) {
      return <span className="proxy-badge">Proxy for [{attendee.proxyForLogin}]</span>;
    }

    const colors = PRIORITY_COLORS[attendee.priority];
    const label = attendee.priority.charAt(0).toUpperCase() + attendee.priority.slice(1);

    if (isEditable && showPrioritySelector) {
      return (
        <span className="priority-selector">
          {[AttendeePriority.Critical, AttendeePriority.Required, AttendeePriority.Optional].map((p, i) => {
            const isActive = attendee.priority === p;
            const pColors = PRIORITY_COLORS[p];
            const pLabel = p.charAt(0).toUpperCase() + p.slice(1);
            return (
              <React.Fragment key={p}>
                {i > 0 && <span className="priority-separator">|</span>}
                <button
                  type="button"
                  className={`priority-option ${isActive ? 'priority-option--active' : ''}`}
                  style={isActive ? { backgroundColor: pColors.background, color: pColors.color, borderColor: pColors.background } : undefined}
                  onClick={() => {
                    handlePriorityChange(p);
                    setShowPrioritySelector(false);
                  }}
                >
                  {pLabel}
                </button>
              </React.Fragment>
            );
          })}
        </span>
      );
    }

    return (
      <span
        className="priority-badge"
        style={{ backgroundColor: colors.background, color: colors.color, cursor: isEditable ? 'pointer' : 'default' }}
        onClick={() => isEditable && setShowPrioritySelector(true)}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="attendee-card-wrapper hoverable-row">
      <Grid gridDefinition={[{ colspan: 6 }, { colspan: 5 }, { colspan: 1 }]}>
        {/* Column 1: Favorite Star + Add Proxy + Profile + Name + Priority */}
        <div className="attendee-column">
          {isProxy ? (
            <span className="favorite-star-spacer" />
          ) : (
            <button
              className="favorite-star-button"
              onClick={() => onToggleFavorite(attendee.id)}
              aria-label={attendee.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              type="button"
            >
              <span className="favorite-star-emoji">{attendee.isFavorite ? '⭐' : <Icon name="star" />}</span>
            </button>
          )}
          {canAddProxy ? (
            <button
              className="favorite-star-button"
              onClick={() => setShowProxySearch((prev) => !prev)}
              aria-label="Add proxy attendee"
              type="button"
              title="Add proxy attendee"
            >
              <FontAwesomeIcon icon={faUserGroup} />
            </button>
          ) : (
            isEditable && <span className="favorite-star-spacer" />
          )}
          {/* Main horizontal row: Profile Picture + Name + Select Participant */}
          {displayEmployee ? (
            <EmployeeListItem employee={displayEmployee} showDetails={!!(email && attendee.selectedEmployee)} disableHover={true} />
          ) : (
            <div className="employee-item employee-item--unresolved">
              <div className="employee-info-container">
                <ProfilePicture loginName="" name={displayName} size="small" />
                <div className="employee-name-wrapper">
                  <div className="employee-name-line">
                    <span className="employee-name-text">{displayName}</span>
                  </div>
                  {email && <div className="employee-details-text">{email}</div>}
                </div>
              </div>
              {/* Participant Selector - for unresolved attendees, positioned as action */}
              <div className="employee-item-actions">
                <ParticipantSelector
                  onEmployeeSelect={handleEmployeeSelect}
                  placeholder={`Search for "${attendee.inputName}"...`}
                  initialQuery={attendee.inputName}
                  existingEmails={existingEmails}
                />
              </div>
            </div>
          )}

          {renderPriority()}

          {/* Error/Warning Messages */}
          {(isDuplicate || attendee.hasError) && (
            <Box margin={{ top: 'xxxs' }}>
              {isDuplicate && <Badge color="red">⚠️ Duplicate</Badge>}
              {attendee.hasError && <Badge color="red">❌ {attendee.errorMessage || 'Not found'}</Badge>}
            </Box>
          )}
        </div>

        {/* Column 2: Executive Assistant Details */}
        <div className="attendee-column attendee-column--ea">
          {attendee.selectedExecutiveAssistant ? (
            <>
              <EmployeeListItem
                employee={attendee.selectedExecutiveAssistant}
                showDetails={!!attendee.selectedExecutiveAssistant.employee_internal_email_address}
                disableHover={true}
              />
              {(() => {
                const leaderEA = LEADER_EA_EMAIL_DATA.find((l) => l.alias.toLowerCase() === attendee.selectedEmployee?.login_name?.toLowerCase());
                return leaderEA?.eaEmailAddress ? (
                  <Box fontSize="body-s" color="text-body-secondary" margin={{ left: 'l' }}>
                    <Icon name="envelope" size="small" /> {leaderEA.eaEmailAddress}
                  </Box>
                ) : null;
              })()}
            </>
          ) : (
            <div className="employee-item">
              <div className="employee-info-container">
                <div style={{ width: '32px', height: '32px' }} />
                <div className="employee-name-wrapper">
                  <Box fontSize="body-s" color={attendee.selectedEmployee ? 'text-body-secondary' : 'text-status-inactive'}>
                    <em>-</em>
                  </Box>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Column 3: Delete button */}
        <div className="attendee-column attendee-column--center">
          {isEditable && (
            <button
              className="favorite-star-button"
              onClick={() => onRemove(attendee.id)}
              aria-label="Delete attendee"
              type="button"
              title="Remove attendee"
            >
              <Icon name="remove" />
            </button>
          )}
        </div>
      </Grid>
      {showProxySearch && canAddProxy && (
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 5 }, { colspan: 1 }]}>
          <div className="proxy-search-wrapper">
            <EmployeeSearchBar
              onAddAttendee={(employee) => {
                onAddProxy!(attendee.id, employee);
                setShowProxySearch(false);
              }}
              existingEmails={existingEmails}
              placeholder="Search for proxy attendee..."
              singleAddButton
              addButtonText={hasProxy ? 'Replace Proxy' : 'Add as Proxy'}
              fullWidth
            />
            <button
              className="favorite-star-button"
              onClick={() => setShowProxySearch(false)}
              aria-label="Close proxy search"
              type="button"
              title="Close"
            >
              <Icon name="close" />
            </button>
          </div>
          <div />
          <div />
        </Grid>
      )}
    </div>
  );
};
